import * as THREE from 'three';
import { lookDotTo, type Xyz } from '../interact/Interact';
import { Grid } from '../inventory/Grid';

export const BOW_DAMAGE = 28;
export const BOW_RANGE = 28;
export const BOW_LOOK_MIN = 0.82;
export const BOW_WOOD = 3;
export const BOW_FIBER = 2;

const _look = new THREE.Vector3();

export type BowTarget = {
  live: boolean;
  position: Xyz;
  takeHit(amount: number): void;
};

export type BowFireCtx = {
  held: string | null;
  camera: THREE.Camera;
  player: Xyz;
  yaw?: number;
  dinos: readonly BowTarget[];
};

export type BowFlags = { bowChip?: boolean };

export function hasBowMats(inv: { countOf(id: string): number }): boolean {
  if (inv.countOf('wood') < BOW_WOOD || inv.countOf('fiber') < BOW_FIBER) {
    return false;
  }
  return inv.countOf('leather') >= 1 || inv.countOf('fiber') >= BOW_FIBER + 1;
}

export function canCraftBow(flags: BowFlags, inv: { countOf(id: string): number }): boolean {
  return !!flags.bowChip && inv.countOf('bow') <= 0 && hasBowMats(inv);
}

export function consumeBowRecipe(inv: Grid): boolean {
  if (!hasBowMats(inv)) {
    return false;
  }
  const useLeather = inv.countOf('leather') >= 1;
  if (!inv.remove('wood', BOW_WOOD) || !inv.remove('fiber', BOW_FIBER)) {
    return false;
  }
  if (useLeather) {
    return inv.remove('leather', 1);
  }
  return inv.remove('fiber', 1);
}

export function craftBow(
  flags: BowFlags,
  bag: { inventory: Grid; give(id: string, n?: number): void },
): boolean {
  if (!canCraftBow(flags, bag.inventory) || !consumeBowRecipe(bag.inventory)) {
    return false;
  }
  bag.give('bow');
  return true;
}

export class Bow {
  drawing = false;
  fired = 0;

  drive(dt: number, holding: boolean, ctx: BowFireCtx): boolean {
    void dt;
    if (ctx.held !== 'bow') {
      this.drawing = false;
      return false;
    }
    if (holding) {
      this.drawing = true;
      return false;
    }
    if (!this.drawing) {
      return false;
    }
    this.drawing = false;
    return this.tryFire(ctx);
  }

  tryFire(ctx: BowFireCtx): boolean {
    if (ctx.held !== 'bow') {
      return false;
    }
    this.fired += 1;
    firstDinoOnRay(ctx.dinos, ctx.camera, ctx.yaw)?.takeHit(BOW_DAMAGE);
    return true;
  }
}

/** Closest live dino on the camera-forward ray, up to 28 m. */
export function firstDinoOnRay(
  dinos: readonly BowTarget[],
  camera: THREE.Camera,
  yaw?: number,
  range = BOW_RANGE,
): BowTarget | null {
  lookDir(camera, yaw, _look);
  const origin = camera.position;
  let best: BowTarget | null = null;
  let bestDist = Infinity;
  for (const dino of dinos) {
    if (!dino.live) {
      continue;
    }
    const dist = Math.hypot(dino.position.x - origin.x, dino.position.y - origin.y, dino.position.z - origin.z);
    if (dist > range || lookDotTo(origin, _look, dino.position) < BOW_LOOK_MIN) {
      continue;
    }
    if (dist < bestDist) {
      best = dino;
      bestDist = dist;
    }
  }
  return best;
}

function lookDir(camera: THREE.Camera, yaw: number | undefined, out: THREE.Vector3): THREE.Vector3 {
  camera.getWorldDirection(out);
  if (out.lengthSq() > 1e-8) {
    return out;
  }
  const facing = yaw ?? 0;
  return out.set(Math.sin(facing), 0, Math.cos(facing));
}
